/** @jsx CanvaSX */
import { CanvaSX, renderElement, withContext } from '@lib/canvasx/canvasx';
import tools from '../tools';
import ShapeBase from './ShapeBase';
// import React from 'react';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { normalizeSpacing } from '@lib/canvasx/shapes';
import stickyNoteIcon from '../assets/icons/stickyNote.svg';
import wideStickyNoteIcon from '../assets/icons/wideStickyNote.svg';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import type {
  EmbeddedTextBehaviors,
  TextProps,
} from '../objects/EmbeddedTextBehaviorsV2';
import { isEndlessStack } from '../objects/guards';
import type Shape from '../objects/Shape';
import { defaultColorForShape } from '../utils/DefaultsManager';
import { svgToIcon } from '../utils/svg';
import { reactionKey } from '../objects/ReactionsBehaviors';
import {
  type ButtonState,
  ButtonStyles,
  formattedRgba,
} from '../canvasx/buttonStyles';
import { Text } from '@lib/canvasx/components/Text';
import { Emoji } from '../canvasx/Emoji';
import { Rect } from '@lib/canvasx/components/Rect';
import { roundRectPaperPath } from './utils';

const DEFAULT_MARGIN = 10;

export const NARROW_NOTE_WIDTH = 100;
export const WIDE_NOTE_WIDTH = 166;
export const NOTE_HEIGHT = 100;
const NARROW_NOTE_WIDTH_FROM_OLD_2X_SCALING = 200;
const WIDE_NOTE_WIDTH_FROM_OLD_2X_SCALING = 333;

const STACK_THICKNESS = 16;
const STACK_FIT_INSIDE = true;

class ShapeNote extends ShapeBase {
  textProps: TextProps = {
    allowedStyles: 'any',
    location: 'inside',
    textAlign: 'center',
    verticalAlign: 'none',
    fontSize: 'scaleToFit',
    overflow: 'clip',
    wrapText: 'wrap',
    margins: 'noScale',
  };

  fabricObject: Shape<ShapeNote> & EmbeddedTextBehaviors;

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.fabricObject.uniformScaling = true;
    this.fabricObject.strokeWidth = 0;
    this.fabricObject.stroke = undefined;

    // HACK: Use setTimeout() because changes made in the constructor are not persisted
    // while loading content from the server
    setTimeout(() => {
      fabricObject.controller.undoManager.withoutUndo(() => {
        // Migrate notes that were created at 2x size and then downscaled, which was removed in AWT-70
        // TODO: remove this migration code if it's still here in one year, ie after 2025-11-20.
        if (
          [
            NARROW_NOTE_WIDTH_FROM_OLD_2X_SCALING,
            WIDE_NOTE_WIDTH_FROM_OLD_2X_SCALING,
          ].includes(fabricObject.width)
        ) {
          // Only need to adjust width because we change the height later
          fabricObject.set(
            'width',
            fabricObject.width === NARROW_NOTE_WIDTH_FROM_OLD_2X_SCALING
              ? NARROW_NOTE_WIDTH
              : WIDE_NOTE_WIDTH
          );
          // Only need to adjust scaleY because later we set scaleX to scaleY if they don't match
          fabricObject.set('scaleY', fabricObject.scaleY * 2);
          if (fabricObject.fontSize) {
            fabricObject.set('fontSize', fabricObject.fontSize / 2);
          }
          // Migration code ends here
        } else if (
          ![NARROW_NOTE_WIDTH, WIDE_NOTE_WIDTH].includes(
            this.fabricObject.width
          )
        ) {
          this.fabricObject.set('width', NARROW_NOTE_WIDTH);
        }
        if (this.fabricObject.height !== NOTE_HEIGHT) {
          this.fabricObject.set('height', NOTE_HEIGHT);
        }

        if (this.fabricObject.scaleX !== this.fabricObject.scaleY) {
          this.fabricObject.set('scaleX', this.fabricObject.scaleY);
        }
      });
    });
  }

  static shapeControls = this.createDefaultControls();
  static renderIcon(width: number = NARROW_NOTE_WIDTH): JSX.Element {
    if (width === NARROW_NOTE_WIDTH) {
      return svgToIcon(stickyNoteIcon);
    } else {
      return svgToIcon(wideStickyNoteIcon);
    }
  }

  static iconClass() {
    return 'fa-regular fa-sticky-note';
  }

  // We use a larger default width for notes to solve a subtle scaling problem.
  // When auto-scaling text, if the base size is too small then the red dots
  // that browsers place under spelling mistakes are too big after scaling up.
  static defaultWidth() {
    return NARROW_NOTE_WIDTH;
  }

  static defaultHeight() {
    return NOTE_HEIGHT;
  }

  calcTextMargin() {
    return {
      top: DEFAULT_MARGIN,
      left: DEFAULT_MARGIN,
      right: DEFAULT_MARGIN,
      bottom: DEFAULT_MARGIN,
    };
  }

  tools() {
    const lastGroup = [tools.ToolFillColor];
    if (!isEndlessStack(this.fabricObject)) {
      lastGroup.push(tools.ToolAddReaction);
    }
    return [
      [tools.ToolShapeType, tools.ToolOpenRecord],
      [tools.ToolEndlessStack],
      lastGroup,
    ];
  }

  allowedStyleCopyPasteFields(): string[] {
    return ['opacity', 'fill'];
  }

  wasSet(keyOrPath: KeyPath, value: unknown) {
    super.wasSet(keyOrPath, value);

    // Delay the updates so that all fields are set during initialization and
    // we only prepare the image once.
    if (keyOrPath === 'width') {
      setTimeout(() => {
        this.fabricObject.endTextEditing();
        void this.fabricObject.initializeText();
        this.fabricObject.synchronize();
      });
    }
  }

  renderSingleShadow(ctx: CanvasRenderingContext2D): void {
    const { width, height } = this;
    const x = -width / 2;
    const y = -height / 2;

    ctx.save();

    // first clip out the area around the shape where we want the shadows to render
    // this means we can draw the shadow without worrying about parts of it showing
    // behind the note
    const shadowClipWidth = height / 10;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + height);
    ctx.lineTo(x + width, y + height);
    ctx.lineTo(x + width, y);
    ctx.lineTo(x - shadowClipWidth, y);
    ctx.lineTo(x - shadowClipWidth, y - shadowClipWidth);
    ctx.lineTo(x + width + shadowClipWidth, y - shadowClipWidth);
    ctx.lineTo(x + width + shadowClipWidth, y + height + shadowClipWidth);
    ctx.lineTo(x - shadowClipWidth, y + height + shadowClipWidth);
    ctx.lineTo(x - shadowClipWidth, y - shadowClipWidth);
    ctx.clip();

    ctx.closePath();
    ctx.clip();

    // BEGIN RIGHT SHADOW
    ctx.beginPath();

    // from the bottom right...
    ctx.moveTo(x + width, y + height);

    // ...curve down to the left
    ctx.quadraticCurveTo(
      x + width * 0.6,
      y + height,
      x + width * 0.06,
      y + height * 1.07
    );

    // finish the path
    ctx.lineTo(x + width * 0.06, y);
    ctx.lineTo(x + width, y);
    ctx.closePath();

    const gradient = ctx.createLinearGradient(x + width, y, x, y);
    gradient.addColorStop(0, `rgba(35, 31, 32, 0.12)`);
    gradient.addColorStop(1, `rgba(35, 31, 32, 0.03)`);

    ctx.fillStyle = gradient;
    ctx.fill();

    // BEGIN LEFT SHADOW
    ctx.beginPath();

    // from the bottom left...
    ctx.moveTo(x, y + height);

    // ...curve down to the right
    ctx.quadraticCurveTo(
      x + width * 0.5,
      y + height * 1.015,
      x + width * 0.975,
      y + height * 1.035
    );

    // finish the path
    ctx.lineTo(x + width * 0.92, y);
    ctx.lineTo(x, y);
    ctx.closePath();

    const gradient2 = ctx.createLinearGradient(x, y, x + width, y);
    gradient2.addColorStop(0.732, `rgba(35, 31, 32, 0.045)`);
    gradient2.addColorStop(0.987, `rgba(35, 31, 32, 0.012)`);

    ctx.fillStyle = gradient2;
    ctx.fill();

    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (this.fabricObject.endlessStack) {
      this.renderStack(ctx);
    } else {
      this.renderSingle(ctx);
    }
  }

  // Render a single sticky note
  renderSingle(ctx: CanvasRenderingContext2D): void {
    const { width, height, fill } = this;

    // Only render the shadow if the note is big enough for it to be visible.
    if (width * this.scaleX * this.canvas.getZoom() > 30) {
      this.renderSingleShadow(ctx);
    }

    const x = -width / 2;
    const y = -height / 2;

    ctx.fillStyle = fill ?? defaultColorForShape('note');

    ctx.fillRect(x, y, width, height);

    if (
      this.fabricObject.controller?.state.showShapeAuthors &&
      !this.fabricObject.textContentIsEmpty()
    ) {
      this.renderAuthor(ctx);
    }

    this.renderReactions(ctx);
  }

  // Render a stack of sticky notes
  renderStack(ctx: CanvasRenderingContext2D): void {
    this.renderStackBackground(ctx);
    this.renderStackShadow(ctx);
    this.renderTopNote(ctx);
    this.renderReactions(ctx);
  }

  renderTopNote(ctx: CanvasRenderingContext2D) {
    let { width, height, fill } = this;
    const x = -width / 2;
    const y = -height / 2;
    if (STACK_FIT_INSIDE) {
      height -= STACK_THICKNESS;
    }

    const noteColor = fill ?? defaultColorForShape('note');
    ctx.fillStyle = noteColor;

    // Shape path is based on original Figma SVG
    ctx.beginPath();
    // Top right
    ctx.moveTo(x + width, y);
    // Top left
    ctx.lineTo(x, y + 0 * height);
    // Bottom left
    ctx.lineTo(x - 0.02 * width, y + 0.95 * height);
    // Bottom right
    ctx.bezierCurveTo(
      x + 0 * width,
      y + 0.95 * height,
      x + 0.841 * width,
      y + 0.951 * height,
      x + 1.05 * width,
      y + 0.84 * height
    );
    // Top right
    ctx.bezierCurveTo(
      x + 1.02 * width,
      y + 0.702 * height,
      x + width,
      y,
      x + width,
      y
    );
    ctx.closePath();
    ctx.fill();
  }

  renderStackShadow(ctx: CanvasRenderingContext2D): void {
    let { width, height } = this;
    const x = -width / 2;
    const y = -height / 2;
    if (STACK_FIT_INSIDE) {
      height -= STACK_THICKNESS;
    }

    const gradient = ctx.createLinearGradient(
      x,
      y + 0.9 * height,
      x,
      y + height
    );
    // Based on colours --theme-shadow-medium and --theme-shadow-shallow
    gradient.addColorStop(0, `rgba(0, 0, 0, 0.2)`);
    gradient.addColorStop(1, `rgba(0, 0, 0, 0.08)`);

    ctx.fillStyle = gradient;
    ctx.fillRect(x, y, width, height);
  }

  renderStackBackground(ctx: CanvasRenderingContext2D) {
    const { width, height, fill } = this;
    const x = -width / 2;
    const y = -height / 2;

    const noteColor = fill ?? defaultColorForShape('note');

    if (STACK_FIT_INSIDE) {
      ctx.fillStyle = noteColor;
      ctx.fillRect(x, y, width, height);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(x, y + height - STACK_THICKNESS, width, STACK_THICKNESS);
    } else {
      ctx.fillStyle = noteColor;
      ctx.fillRect(x, y, width, height + STACK_THICKNESS);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(x, y + height, width, STACK_THICKNESS);
    }
  }

  _getReactionsToRender() {
    const object = this.fabricObject;

    const reactions = object.reactionVotes;
    if (!reactions || Object.keys(reactions).length === 0) {
      return [];
    }

    let reactionCount = 0;

    return Object.entries(reactions)
      .map(([shortCode, votes]) => {
        // Don't render more than 6 reactions at once.
        if (votes === 0 || reactionCount >= 6) {
          return null;
        }
        reactionCount++;

        const key = reactionKey(shortCode);
        const hovering = object.hoveredKey === key;
        const selected = object.isSelected();

        let buttonState: ButtonState = 'default';
        if (hovering) {
          buttonState = 'hovered';
        } else if (selected) {
          buttonState = 'selected';
        }

        const { background, icon } = ButtonStyles[buttonState];

        return (
          <Rect
            fill={formattedRgba(background)}
            borderRadius={2}
            padding={2}
            key={key}
            onClick={() => {
              const { userId } = object?.controller?.state;
              if (userId) {
                object.toggleReaction({
                  shortCode,
                  userId,
                });

                object.updateTooltipAfterRender(shortCode);
              }
            }}
            tooltip={object.reactionsTooltipContent}
          >
            <FlexRow gap={2} verticalAlignment='center'>
              <Emoji shortCode={shortCode} />
              <Text color={formattedRgba(icon)}>{votes}</Text>
            </FlexRow>
          </Rect>
        );
      })
      .filter(Boolean);
  }

  renderReactions(ctx: CanvasRenderingContext2D) {
    const object = this.fabricObject;
    if (!object.reactions) {
      return;
    }

    const { scaleX, scaleY } = object;
    const scale = Math.min(scaleX, scaleY);

    const maxHeight = 50;

    const x = -object.width / 2;
    const y = -object.height / 2;
    const width = (object.width * scaleX) / scale;
    const height = (object.height * scaleY) / scale;
    const padding = 3;

    const normalizedPadding = normalizeSpacing(padding);

    ctx.save();
    // Reset alpha so that reactions are always 100% visible.
    ctx.globalAlpha = 1;
    ctx.translate(x, y);

    withContext({ canvas: object.canvas, ctx, object, scale }, () => {
      ctx.scale(1 / scaleX, 1 / scaleY);

      renderElement(
        <FlexRow
          gap={{ horizontal: 3, vertical: 4 }}
          width={width}
          height={height}
          padding={normalizedPadding}
          alignBottom
          maxHeight={Math.min(maxHeight, height * scaleY, 1)}
          maxRows={2}
          verticalAlignment='center'
        >
          {this._getReactionsToRender()}
        </FlexRow>
      );
    });

    ctx.restore();
  }

  edgePaperPath(): paper.Path {
    return roundRectPaperPath(this.fabricObject);
  }
}

export default ShapeNote;
